import { useEffect, useState } from "react";
import type { World, WorldManifest } from "@cimara/world";
import type { Me, RaceSummary } from "../api/types";
import {
  categoryRule,
  formatStartAt,
  formatStartIn,
  humanCapacity,
  nextRaceOf,
  phaseLabel,
} from "../race/format";
import { AppHeader } from "./AppHeader";
import { CourseMap } from "./CourseMap";

interface HomeScreenProps {
  world: World | null;
  manifest: WorldManifest | null;
  loading: boolean;
  error: string | null;
  me: Me | null;
  sessionLoading: boolean;
  sessionError: string | null;
  races: RaceSummary[];
  racesLoading: boolean;
  racesError: string | null;
  notice: string | null;
  onRetry(): void;
  onRefreshRaces(): void;
  onSignIn(): void;
  onSignOut(): void;
  onProfile(): void;
  onHistory(): void;
  onOpenRace(race: RaceSummary): void;
  onFreeRide(): void;
  onGuestRide(): void;
}

function km(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return "—";
  return (meters / 1000).toFixed(1);
}

function RaceCard({
  race,
  onOpen,
}: {
  race: RaceSummary;
  onOpen(race: RaceSummary): void;
}) {
  const humanSlots = humanCapacity(race.capacity, race.botCount) || race.capacity;
  const full = humanSlots > 0 && race.registered >= humanSlots;
  return (
    <button
      type="button"
      className={`race-card race-card--${race.phase}`}
      data-testid="race-card"
      onClick={() => onOpen(race)}
    >
      <div className="race-card__top">
        <span className={`phase-chip phase-chip--${race.phase}`}>{phaseLabel(race.phase)}</span>
        <span className="race-card__start">{formatStartAt(race.startAt)}</span>
      </div>
      <h3 className="race-card__name">{race.name}</h3>
      <p className="race-card__route">
        {race.routeName} · {race.laps} {race.laps === 1 ? "lap" : "laps"}
        {race.distanceM ? ` · ${km(race.distanceM)} km` : ""}
      </p>
      <div className="race-card__bottom">
        <div className="race-card__categories">
          {race.categories.map((category) => (
            <span className="category-chip" key={category}>
              {category}
            </span>
          ))}
          {race.categories.length === 0 ? (
            <span className="category-chip category-chip--muted">Open</span>
          ) : null}
        </div>
        <span className={`race-card__field ${full ? "race-card__field--full" : ""}`}>
          {race.registered} / {humanSlots} registered
        </span>
      </div>
    </button>
  );
}

export function HomeScreen({
  world,
  manifest,
  loading,
  error,
  me,
  sessionLoading,
  sessionError,
  races,
  racesLoading,
  racesError,
  notice,
  onRetry,
  onRefreshRaces,
  onSignIn,
  onSignOut,
  onProfile,
  onHistory,
  onOpenRace,
  onFreeRide,
  onGuestRide,
}: HomeScreenProps) {
  const [now, setNow] = useState(() => Date.now());
  const route = world?.routes[0] ?? manifest?.route ?? null;
  const hasWorld = world !== null;
  const nextRace = nextRaceOf(races, now);
  const nextRaceSlots = nextRace
    ? humanCapacity(nextRace.capacity, nextRace.botCount) || nextRace.capacity
    : 0;
  const signedIn = me !== null;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="start-screen home-screen">
      <div className="start-screen__glow" aria-hidden="true" />
      <div className="start-screen__grid" aria-hidden="true" />

      <AppHeader
        me={me}
        onHome={() => undefined}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        onProfile={onProfile}
        onHistory={onHistory}
      />

      <div className="start-layout">
        <section className="start-copy">
          <p className="eyebrow">
            <span className="eyebrow__line" />
            Virtual cycling, race first
          </p>
          <h1 className="wordmark wordmark--hero">
            CIMARA
            <span className="wordmark__period">.</span>
          </h1>
          <p className="tagline">Race the island.</p>
          <p className="lede">
            A scheduled criterium, live gaps, and a field that is already moving.
            Power is the only input; trainer resistance is a comfort layer.
          </p>

          <div className="start-actions">
            <button
              className="ride-button"
              data-testid="start-ride"
              onClick={onFreeRide}
              type="button"
            >
              <span>{signedIn ? "Free ride" : "Ride now"}</span>
              <span className="ride-button__arrow" aria-hidden="true">
                →
              </span>
            </button>
            {!signedIn ? (
              <button
                type="button"
                className="ride-button ride-button--ghost"
                data-testid="guest-ride"
                onClick={onGuestRide}
              >
                Guest free ride
              </button>
            ) : (
              <button type="button" className="text-button" onClick={onProfile}>
                Edit profile
              </button>
            )}
          </div>
          <p className="start-hint">
            {signedIn
              ? "Free ride uses your account. Races appear on the calendar below."
              : sessionLoading
                ? "Checking your session…"
                : "No account needed for a free ride. Racing requires a sign-in."}
          </p>

          {sessionError ? (
            <div className="load-error" role="alert">
              <p>{sessionError}</p>
              <button type="button" className="text-button" onClick={onSignIn}>
                Try signing in
              </button>
            </div>
          ) : null}

          {notice ? (
            <p className="home-notice" role="status">
              {notice}
            </p>
          ) : null}

          {error ? (
            <div className="load-error" role="alert">
              <p>{error}</p>
              <button type="button" className="text-button" onClick={onRetry}>
                Retry world load
              </button>
            </div>
          ) : null}

          <dl className="course-stats">
            <div>
              <dt>Free ride</dt>
              <dd>{route ? `${km(route.distanceM)} km` : "15.0 km"}</dd>
            </div>
            <div>
              <dt>Climb</dt>
              <dd>{route ? `${Math.round(route.climbM)} m` : "243 m"}</dd>
            </div>
            <div>
              <dt>Races</dt>
              <dd>{racesLoading ? "…" : races.length}</dd>
            </div>
            <div>
              <dt>World</dt>
              <dd>{world?.version ?? (loading ? "…" : "—")}</dd>
            </div>
          </dl>
        </section>

        <section className="start-visual" aria-label="Circuit preview">
          <div className="start-visual__frame">
            <CourseMap world={world} />
            <div className="start-visual__meta">
              <span>{route?.name ?? "Phase 0 Circuit"}</span>
              <span>{route ? `${route.climbM.toFixed(0)} m ascent` : "loading"}</span>
            </div>
          </div>

          <div className="next-race" data-testid="next-race">
            <div className="next-race__head">
              <span className="panel-kicker">Next race</span>
              {nextRace ? (
                <span className={`phase-chip phase-chip--${nextRace.phase}`}>
                  {phaseLabel(nextRace.phase)}
                </span>
              ) : null}
            </div>
            {nextRace ? (
              <>
                <button
                  type="button"
                  className="next-race__body"
                  onClick={() => onOpenRace(nextRace)}
                >
                  <span className="next-race__name">{nextRace.name}</span>
                  <span className="next-race__meta">
                    {formatStartIn(nextRace.startAt, now)} · {nextRace.laps} laps ·{" "}
                    {nextRace.registered} / {nextRaceSlots} registered
                  </span>
                  <span className="next-race__categories">
                    {nextRace.categories.map((category) => (
                      <span className="category-chip" key={category}>
                        {category}
                      </span>
                    ))}
                  </span>
                </button>
                <span className="next-race__hint">
                  {signedIn ? "Open the race to register." : "Sign in to register for this race."}
                </span>
              </>
            ) : (
              <p className="next-race__empty">
                {racesLoading
                  ? "Loading the calendar…"
                  : racesError ?? "No races are scheduled yet. The free ride is open."}
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="home-calendar" aria-label="Race calendar">
        <div className="section-head">
          <div>
            <p className="panel-kicker">Calendar</p>
            <h2>Upcoming races</h2>
          </div>
          <button type="button" className="text-button" onClick={onRefreshRaces}>
            Refresh
          </button>
        </div>

        {racesError ? (
          <div className="load-error" role="alert">
            <p>{racesError}</p>
            <button type="button" className="text-button" onClick={onRefreshRaces}>
              Try again
            </button>
          </div>
        ) : null}

        {racesLoading && races.length === 0 ? (
          <p className="calendar-empty">Reading the start list…</p>
        ) : races.length === 0 ? (
          <p className="calendar-empty">
            No races on the calendar. The free ride and guest ride are open.
          </p>
        ) : (
          <div className="race-list">
            {races.map((race) => (
              <RaceCard key={race.id} race={race} onOpen={onOpenRace} />
            ))}
          </div>
        )}

        <div className="home-notes">
          <div>
            <span className="panel-kicker">Categories</span>
            <p>
              A {categoryRule("A")} · B {categoryRule("B")} · C {categoryRule("C")} · D{" "}
              {categoryRule("D")}. Self-select for your first three races.
            </p>
          </div>
          <div>
            <span className="panel-kicker">Guest</span>
            <p>
              A guest can free ride the circuit, but cannot register, appear in results,
              or use history.
            </p>
          </div>
        </div>
      </section>

      <footer className="start-footer">
        <span>
          Cimara — a browser-native cycling world ·{" "}
          <a href="/privacy.html" target="_blank" rel="noreferrer">
            Privacy &amp; terms
          </a>
        </span>
        <span className="start-footer__keys">
          <kbd>Chrome</kbd> or <kbd>Edge</kbd> · Web Bluetooth for FTMS trainers
        </span>
      </footer>
    </main>
  );
}
